import { EITAA_BASE_URL } from '@/utils/constant'
import { createElement } from 'react';

export const isEitaaMiniApp = (): boolean => {
  if (typeof window === "undefined") return false;

  return !!(
    window.Eitaa &&
    window.Eitaa.WebApp &&
    window.Eitaa.WebApp.platform &&
    window.Eitaa.WebApp.platform !== "unknown"
  );
};

const isUnsupportedWebAppMethodError = (error: unknown) =>
  error instanceof Error
    ? error.message === "WebAppMethodUnsupported" || error.name === "WebAppMethodUnsupported"
    : error === "WebAppMethodUnsupported";

export const safeCallEitaaWebApp = (callback: () => void): boolean => {
  try {
    callback();
    return true;
  } catch (error) {
    if (!isUnsupportedWebAppMethodError(error)) {
      console.warn("[EitaaWebApp] method failed", error);
    }
    return false;
  }
};

export const calculateVAT = (price: number | null, specialPrice: number | null): {
  hasVAT: boolean;
  priceWithVAT: number | null;
} => {
  const basePrice = specialPrice ?? price;

  if (basePrice === null || basePrice <= 0) {
    return { hasVAT: false, priceWithVAT: null };
  }
  return {
    hasVAT: true,
    priceWithVAT: Math.round(basePrice * 1.1) // 10% VAT
  };
};



export const openEitaaLink = (text: string) => {
  if (window.Eitaa?.WebApp?.platform !== "unknown" && text) {
    const cleanUsername = text.replace(/^@/, "");
    window.Eitaa.WebApp.openEitaaLink(`${EITAA_BASE_URL}/${cleanUsername}`);
  }
};


export const openUrl = (postUrl?: string) => {
  if (!postUrl) return;
  try {
    if (window.Eitaa?.WebApp?.openEitaaLink) {
      window.Eitaa.WebApp.openEitaaLink(postUrl);
    } else {
      window.open(postUrl, "_blank");
    }
  } catch (e) {
    console.error("Cannot open Eitaa link:", e);
    window.open(postUrl, "_blank");
  }
};

export const isAndroidMiniapp = (): boolean => {
  if (typeof window === "undefined") return false;

  return window.Eitaa?.WebApp?.platform === "android";
}



export function parseLinksAndMentions(text: string): (string | React.ReactElement)[] {
  if (!text) return [text];

  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let key = 0;

  // Combined regex to match URLs and mentions
  // URLs: http://, https://, or www.
  // Mentions: @ followed by alphanumeric characters or underscores (not dots to avoid matching emails)
  const combinedRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|@[\w]+)/gi;

  let match;
  const matches: Array<{ index: number; text: string; type: 'url' | 'mention' }> = [];

  while ((match = combinedRegex.exec(text)) !== null) {
    const matchedText = match[0];
    const isUrl = matchedText.startsWith('http://') ||
      matchedText.startsWith('https://') ||
      matchedText.startsWith('www.');

    matches.push({
      index: match.index,
      text: matchedText,
      type: isUrl ? 'url' : 'mention'
    });
  }

  // Process each match
  for (const matchItem of matches) {
    // Add text before match
    if (matchItem.index > lastIndex) {
      parts.push(text.substring(lastIndex, matchItem.index));
    }

    // Create clickable element
    if (matchItem.type === 'url') {
      let url = matchItem.text;
      if (url.startsWith('www.')) {
        url = 'https://' + url;
      }

      parts.push(
        createElement('a', {
          key: `link-${key++}`,
          href: url,
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            openUrl(url);
          },
          dir: 'ltr',
          className: 'text-blue-500 underline hover:opacity-80 cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer'
        }, matchItem.text)
      );
    } else {
      // Mention
      const username = matchItem.text.substring(1); // Remove @
      const mentionUrl = `https://eitaa.com/${username}`;

      parts.push(
        createElement('a', {
          key: `mention-${key++}`,
          href: mentionUrl,
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            openUrl(mentionUrl);
          },
          dir: 'ltr',
          className: 'text-blue-500 underline hover:opacity-80 cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer'
        }, matchItem.text)
      );
    }

    lastIndex = matchItem.index + matchItem.text.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}



export function shareOnEitaa(message: string) {
  if (
    typeof window !== "undefined" &&
    window.Eitaa &&
    window.Eitaa.WebApp.platform !== "unknown"
  ) {
    const encodedMessage = encodeURIComponent(message);
    window.Eitaa.WebApp.openEitaaLink(
      `https://eitaa.com/share/url?url=${encodedMessage}`,
    );
  }
}

/**
 * Download a file via the Eitaa mini-app's native downloader when available,
 * falling back to a normal browser download on the web.
 */
export function downloadViaEitaa(url: string, fileName?: string): void {
  if (!url) return;
  const webApp = typeof window !== "undefined" ? window.Eitaa?.WebApp : undefined;
  if (webApp?.downloadFile && webApp.platform && webApp.platform !== "unknown") {
    const handled = safeCallEitaaWebApp(() =>
      webApp.downloadFile({ url, file_name: fileName || "download" }),
    );
    if (handled) return;
  }
  if (typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
